import React, { useEffect, useState, useMemo, useCallback, memo } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { addRedesign, clearRedesign, fetchRedesignByCaseId } from "../../store/admin/redesign";
import { RedesignCases } from "../../types/RedesignCases";
import { useParams } from "react-router-dom";
import { fetchCaseFilesByCaseId } from "../../store/common/fetchBoxFileSlice";
import { clearSelectedCase } from "../../store/user/userSlice";

const PRIORITY_OPTIONS = ["High", "Medium"];

const REDESIGN_SCOPES = ["Minor", "Major", "New Design"] as const;
type RedesignScope = (typeof REDESIGN_SCOPES)[number];

const REDESIGN_CATEGORIES = [
  "Query",
  "Design Preference",
  "Note Missed",
  "Design Mistake",
  "Files Mistake",
  "Client Preference Missed",
  "Need to Improve Design Quality",
  "Others (note required)",
];

const Redesign = () => {
  const { case_id } = useParams<{ case_id: string }>();
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const { selectedCase } = useAppSelector((state) => state.adminCases);
  const { isLoading, error, caseRedesigns } = useAppSelector(
    (s) => s.redesignCases
  );
  const { caseFiles, fetching } = useAppSelector((s) => s.fileUpload);

  const [priority, setPriority] = useState("Medium");
  const [selectedAdminFolders, setSelectedAdminFolders] = useState<string[]>(
    []
  );
  const [selectedClientFiles, setSelectedClientFiles] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [redesignScope, setRedesignScope] = useState<"" | RedesignScope>("");
  const [showAll, setShowAll] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [reason, setReason] = useState("");

  const isReadOnly = useMemo(
    () => ["Admin Read", "Production", "Side Admin"].includes(user?.role || ""),
    [user]
  );

  // Fetch all once
useEffect(() => {
  if (!case_id) return;

  dispatch(clearSelectedCase());
  dispatch(clearRedesign()); // ✅ IMPORTANT

  // dispatch(fetchCaseById(case_id));
  dispatch(fetchCaseFilesByCaseId({ case_id }));
  dispatch(fetchRedesignByCaseId(case_id));
}, [case_id, dispatch]);

  const uniqueFolders = useMemo(() => {
    const folders = caseFiles
      .filter((f) => f.case_id === case_id && f.folder_name)
      .map((f) => f.folder_name);
    return [...new Set(folders)];
  }, [caseFiles, case_id]);

  const clientFiles = useMemo(
    () =>
      caseFiles
        .filter((f) => f.file_type === "CLIENT_UPLOAD")
        .map((f) => f.file_name),
    [caseFiles]
  );

  useEffect(() => {
    if (uniqueFolders.length === 1) setSelectedAdminFolders([uniqueFolders[0]]);
  }, [uniqueFolders]);

  const toggleAdminFolder = useCallback((folder: string, checked: boolean) => {
    setSelectedAdminFolders((prev) =>
      checked ? [...prev, folder] : prev.filter((f) => f !== folder)
    );
  }, []);

  const toggleClientFile = useCallback((file: string, checked: boolean) => {
    setSelectedClientFiles((prev) =>
      checked ? [...prev, file] : prev.filter((f) => f !== file)
    );
  }, []);

  const toggleCategories = useCallback((file: string, checked: boolean) => {
    setSelectedCategories((prev) =>
      checked ? [...prev, file] : prev.filter((f) => f !== file)
    );
  }, []);

  const handleSend = useCallback(async () => {
    if (!case_id) return;
    const isOtherSelected = selectedCategories.some((c) =>
      c.startsWith("Others")
    );

    // Pre-submit validation
    if (!redesignScope) {
      return;
    }
    if (selectedCategories.length === 0) {
      return;
    }
    if (isOtherSelected && !reason.trim()) {
      alert("Please provide a note when selecting 'Others' category.");
      return;
    }

    setSending(true);
    try {
      await dispatch(
        addRedesign({
          case_id,
          user_id: user?.user_id || 11,
          priority,
          status: "to be downloaded",
          category: [redesignScope, ...selectedCategories].join(","),
          adminUploads: selectedAdminFolders.join(","),
          clientUploads: selectedClientFiles.join(","),
          note: reason || "No reason provided",
          sendBy: user?.role === "Super Admin" ? user?.user_name : employee?.emp_name || "",
        })
      );
      await dispatch(fetchRedesignByCaseId(case_id));
    } finally {
      setSending(false);
      setShowConfirm(false);
      setPriority("Medium");
      setSelectedAdminFolders([]);
      setSelectedClientFiles([]);
      setSelectedCategories([]);
      setRedesignScope("");
      setReason("");
    }
  }, [
    case_id,
    dispatch,
    priority,
    selectedAdminFolders,
    selectedClientFiles,
    selectedCategories,
    redesignScope,
    reason,
  ]);

  const sortedRedesigns = useMemo(
    () =>
      Array.isArray(caseRedesigns)
        ? [...caseRedesigns].sort(
          (a, b) =>
            new Date(b.creation_time).getTime() -
            new Date(a.creation_time).getTime()
        )
        : [],
    [caseRedesigns]
  );

  const handleSelectAllClient = (checked: boolean) => {
    setSelectedClientFiles(checked ? [...clientFiles] : []);
  };

  const handleSelectAllAdminFolders = (checked: boolean) => {
    setSelectedAdminFolders(checked ? [...uniqueFolders] : []);
  };

  const handleSelectAllCategories = (checked: boolean) => {
    setSelectedCategories(checked ? [...REDESIGN_CATEGORIES] : []);
  };

  const isOtherSelected = selectedCategories.some((c) =>
    c.startsWith("Others")
  );

  const isSendDisabled =
    ["files pending", "submitted"].includes(
      (selectedCase?.status ?? "").toLowerCase()
    ) ||
    selectedAdminFolders.length === 0 ||
    selectedClientFiles.length === 0 ||
    !redesignScope ||
    selectedCategories.length === 0 ||
    (isOtherSelected && reason.trim() === "");

  return (
    <div className="w-full flex flex-col lg:flex-row gap-6">
      {!isReadOnly && (
        <div className="bg-white p-4 rounded-xl shadow text-sm w-full max-w-lg space-y-4">
          <div className="w-full flex gap-8">
            <CheckboxList
              label="Select Client Uploads"
              options={clientFiles}
              selected={selectedClientFiles}
              onToggle={toggleClientFile}
              onSelectAll={handleSelectAllClient}
            />

            {uniqueFolders.length > 0 && (
              <CheckboxList
                label="Select Patient Folder"
                options={uniqueFolders}
                selected={selectedAdminFolders}
                onToggle={toggleAdminFolder}
                onSelectAll={handleSelectAllAdminFolders}
              />
            )}
          </div>

          <div>
            <p className="block text-gray-700 font-medium mb-2">
              Select Redesign Type
            </p>
            <div className="grid grid-cols-3 gap-2">
              {REDESIGN_SCOPES.map((option) => {
                const selected = redesignScope === option;
                return (
                  <label
                    key={option}
                    className={`flex items-center gap-2 border rounded-md p-2 cursor-pointer ${
                      selected
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="redesign-scope"
                      value={option}
                      checked={selected}
                      onChange={() => setRedesignScope(option)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="cursor-pointer">{option}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <CheckboxList
              label="Select Redesign Category"
              options={REDESIGN_CATEGORIES}
              selected={selectedCategories}
              onToggle={toggleCategories}
              onSelectAll={handleSelectAllCategories}
              gridView={true}
            />
          </div>

          <div className="space-y-1">
            <label className="block text-gray-700 text-sm font-medium">
              Note
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Enter note for redesign"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-gray-700 font-medium">Priority</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500"
            >
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p}>{p}</option>
              ))}
            </select>
          </div>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <button
            onClick={() => setShowConfirm(true)}
            disabled={isSendDisabled}
            className={`w-full text-white px-4 py-2 rounded-md transition ${isSendDisabled
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-red-500 hover:bg-red-600"
              }`}
          >
            {sending ? "Sending..." : "Send for Redesign"}
          </button>
        </div>
      )}

      <div
        className={`${isReadOnly ? "w-full" : "flex-1"
          } bg-white p-4 rounded-xl shadow text-sm space-y-4 max-h-[400px] overflow-y-auto`}
      >
        <h2 className="text-base font-semibold text-gray-800">
          Redesign History
        </h2>

        <RedesignHistory
          redesigns={sortedRedesigns}
          loading={isLoading || fetching}
          showAll={showAll}
          setShowAll={setShowAll}
        />
      </div>

      <ConfirmModal
        show={showConfirm}
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleSend}
        sending={sending}
        priority={priority}
        clientFiles={selectedClientFiles}
        reason={reason}
        setReason={setReason}
      />
    </div>
  );
};

export default Redesign;

/* ---------------------------- Reusable Components --------------------------- */

const CheckboxList = memo(
  ({
    label,
    options,
    selected,
    onToggle,
    gridView = false,
    onSelectAll,
  }: {
    label: string;
    options: string[];
    selected: string[];
    onToggle: (value: string, checked: boolean) => void;
    gridView?: boolean;
    onSelectAll?: (checked: boolean) => void;
  }) => {
    const allSelected = selected.length === options.length;
    const partiallySelected = selected.length > 0 && !allSelected;

    return (
      <div className="space-y-2 w-full h-full">
        <label className="block text-gray-700 font-medium">{label}</label>

        {/* --- Select All --- */}
        <div className="flex items-center space-x-2 mb-1">
          <input
            id={`select-all-${label}`}
            type="checkbox"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = partiallySelected;
            }}
            onChange={(e) => {
              if (onSelectAll) {
                onSelectAll(e.target.checked);
              } else {
                // fallback behavior
                options.forEach((item) => onToggle(item, e.target.checked));
              }
            }}
            className="rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
          />

          <label htmlFor={`select-all-${label}`} className="cursor-pointer">
            Select All
          </label>
        </div>

        {/* --- Options --- */}
        <div
          className={`border border-gray-300 rounded-md p-2 overflow-y-auto ${
            gridView ? "grid grid-cols-2 gap-1" : ""
          }`}
        >
          {options.map((item) => (
            <div key={item} className="flex items-center space-x-2">
              <input
                id={`checkbox-${item}`}
                type="checkbox"
                checked={selected.includes(item)}
                onChange={(e) => onToggle(item, e.target.checked)}
                className="rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
              <label
                htmlFor={`checkbox-${item}`}
                className="cursor-pointer break-all"
              >
                {item}
              </label>
            </div>
          ))}
        </div>
      </div>
    );
  }
);

const ConfirmModal = memo(
  ({
    show,
    onCancel,
    onConfirm,
    sending,
    priority,
    clientFiles,
  }: {
    show: boolean;
    onCancel: () => void;
    onConfirm: () => void;
    sending: boolean;
    priority: string;
    clientFiles: string[];
    reason: string;
    setReason: (val: string) => void;
  }) => {
    if (!show) return null;
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
        <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Confirm Redesign Request
          </h3>
          <p className="text-sm text-gray-600">
            Are you sure you want to send this redesign with priority{" "}
            <span className="font-semibold">{priority}</span>
            {clientFiles.length > 0 && (
              <>
                {" "}
                for patient{" "}
                <span className="font-semibold">
                  {clientFiles.map((f) => f.replace(/\.zip$/i, "")).join(", ")}
                </span>
                ?
              </>
            )}
          </p>

          <div className="flex justify-end gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={sending}
              className={`${sending
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-red-500 hover:bg-red-600"
                } text-white px-4 py-2 rounded-md`}
            >
              {sending ? "Sending..." : "Confirm"}
            </button>
          </div>
        </div>
      </div>
    );
  }
);

const RedesignHistory = memo(
  ({
    redesigns,
    loading,
    showAll,
    setShowAll,
  }: {
    redesigns: RedesignCases[];
    loading: boolean;
    showAll: boolean;
    setShowAll: React.Dispatch<React.SetStateAction<boolean>>;
  }) => {
    const visible = showAll ? redesigns : redesigns.slice(0, 3);

    if (loading)
      return <p className="text-gray-500 italic">Loading redesigns...</p>;
    if (!redesigns.length)
      return <p className="text-gray-500">No redesigns yet.</p>;

    return (
      <>
        {visible.map((r, idx) => (
          <div
            key={idx}
            className="border border-gray-200 rounded-md p-3 space-y-1 bg-gray-50"
          >
            <div className="grid grid-cols-2">
              <p>
                <strong>Attempt:</strong> {r.redesign_attempt}
              </p>
              <p>
                <strong>Priority:</strong> {r.priority}
              </p>
            </div>
            <div>
              <strong>Status:</strong> {r.status}
            </div>
            <div>
              <strong>Category:</strong> {r.category}
            </div>
            <div>
              <strong>Time:</strong>{" "}
              {new Date(r.creation_time).toLocaleString()}
            </div>
            <div>
              <strong>Patient Folder:</strong> {r.adminUploads}
            </div>
            <div>
              <strong>Client Uploads:</strong> {r.clientUploads}
            </div>
            <div className="col-span-2">
              <strong>Note:</strong> {r.note}
            </div>
          </div>
        ))}

        {redesigns.length > 3 && (
          <div className="text-center mt-2">
            <button
              onClick={() => setShowAll((prev) => !prev)}
              className="text-blue-600 hover:underline text-xs"
            >
              {showAll ? "Show less" : "Load more redesigns"}
            </button>
          </div>
        )}
      </>
    );
  }
);
